<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractRevisionDropdown',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    contract: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const otherRevisions = (...args) => {
    return _.filter(args[0], (p) => p.revision_number != args[1]);
  };

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedRevision = computed(() => {
    return _.find(props.contract.revisions, {
      revision_number: props.contract.revision_number,
    });
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );

  const act_8358196963355853_navigate_hlc5o84gt_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.contract',
        query: {
          rev: $event.scope.revision.revision_number,
        },
        params: {
          id: props.contract.id,
        },
      },
    };
  };
  const act_8356612450665535_showModal_wv6in4qu1_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateContractRevision',
      props: {
        contract: _.clone(props.contract),
      },
    };
  };
  const act_1382594685840889_closeComponent_neeunj7k4_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1382594685840889_request_bs5z3l24z_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_contract',
      data: props.contract,
      parameters: {
        rev: props.contract.revision_number,
      },
    };
  };
  const act_1382594685840889_request_bs5z3l24z_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_8358196963355853_navigate_hlc5o84gt =
    new actionTypes.NavigateAction({
      actionArgs: act_8358196963355853_navigate_hlc5o84gt_getActionArgs,
    });
  const inst_evh_8358196963355853_clickPreventStop_jsxe5m8vu =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_navigate_hlc5o84gt,
      event: 'click.prevent.stop',
      displayName: 'navigate',
    });
  const inst_evh_8358196963355853_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8358196963355853_clickPreventStop_jsxe5m8vu],
      event: 'click.prevent.stop',
    });
  const act_8356612450665535_reloadComponentData_v6a7l4z1j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8356612450665535_success_n7qi1hdk0 =
    new actionTypes.EventHandlerImpl({
      action: act_8356612450665535_reloadComponentData_v6a7l4z1j,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_8356612450665535_showModal_wv6in4qu1 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8356612450665535_showModal_wv6in4qu1_getActionArgs,
      events: [inst_evh_8356612450665535_success_n7qi1hdk0],
    });
  const inst_evh_8356612450665535_click_bchk0vcbq =
    new actionTypes.EventHandlerImpl({
      action: act_8356612450665535_showModal_wv6in4qu1,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_8356612450665535_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8356612450665535_click_bchk0vcbq],
    event: 'click',
  });
  const act_1382594685840889_closeComponent_neeunj7k4 =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1382594685840889_closeComponent_neeunj7k4_getActionArgs,
    });
  const inst_evh_1382594685840889_success_lckf29hig =
    new actionTypes.EventHandlerImpl({
      action: act_1382594685840889_closeComponent_neeunj7k4,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1382594685840889_request_bs5z3l24z = new actionTypes.RequestAction({
    actionArgs: act_1382594685840889_request_bs5z3l24z_getActionArgs,
    confirm: act_1382594685840889_request_bs5z3l24z_getConfirm,
    events: [inst_evh_1382594685840889_success_lckf29hig],
  });
  const inst_evh_1382594685840889_click_wbilv9rj4 =
    new actionTypes.EventHandlerImpl({
      action: act_1382594685840889_request_bs5z3l24z,
      event: 'click',
      displayName: 'request',
    });
  const inst_evh_1382594685840889_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1382594685840889_click_wbilv9rj4],
    event: 'click',
  });

  const actions = {
    inst_evh_8358196963355853_clickPreventStop_jsxe5m8vu,
    inst_evh_8358196963355853_clickPreventStop,
    inst_evh_8356612450665535_success_n7qi1hdk0,
    inst_evh_8356612450665535_click_bchk0vcbq,
    inst_evh_8356612450665535_click,
    inst_evh_1382594685840889_success_lckf29hig,
    inst_evh_1382594685840889_click_wbilv9rj4,
    inst_evh_1382594685840889_click,
  };

  const evh_8358196963355853_clickPreventStop_jsxe5m8vu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8358196963355853_clickPreventStop_jsxe5m8vu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8356612450665535_success_n7qi1hdk0 = async (event, scope) => {
    return await actions.inst_evh_8356612450665535_success_n7qi1hdk0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8356612450665535_click_bchk0vcbq = async (event, scope) => {
    return await actions.inst_evh_8356612450665535_click_bchk0vcbq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8356612450665535_click = async (event, scope) => {
    return await actions.inst_evh_8356612450665535_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1382594685840889_success_lckf29hig = async (event, scope) => {
    return await actions.inst_evh_1382594685840889_success_lckf29hig.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1382594685840889_click_wbilv9rj4 = async (event, scope) => {
    return await actions.inst_evh_1382594685840889_click_wbilv9rj4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1382594685840889_click = async (event, scope) => {
    return await actions.inst_evh_1382594685840889_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <BDropdown size="sm" toggle-class="btn-light border" v-if="loaded">
    <template #button-content>
      <UcContractRevisionDropdownItem :revisionInfo="selectedRevision">
      </UcContractRevisionDropdownItem>
    </template>
    <hr v-if="contract.revisions.length > 1" />
    <BDropdownItem
      :key="revision.revision_number"
      v-for="revision in _.orderBy(contract.revisions, ['revision_number'])"
      @click.prevent.stop="
        evh_8358196963355853_clickPreventStop($event, { revision })
      "
    >
      <UcContractRevisionDropdownItem
        :revisionInfo="revision"
        :isSelected="contract.revision_number == revision.revision_number"
      >
      </UcContractRevisionDropdownItem>
    </BDropdownItem>
    <hr />
    <BDropdownItem @click="evh_8356612450665535_click($event, {})">
      <ItpText :text="pgettext('Contract', 'Create revision...')"> </ItpText>
    </BDropdownItem>
    <BDropdownItem
      v-if="contract.metadata.can_delete_revision"
      @click="evh_1382594685840889_click($event, {})"
    >
      <ItpText :text="pgettext('Contract', 'Delete revision')"> </ItpText>
    </BDropdownItem>
  </BDropdown>
</template>
